import { useEffect, useState } from "react";
import { openExternal } from "../lib/open-external";
import { Star, Circle, ExternalLink, Sparkles } from "lucide-react";
import { T, scoreColor, withAlpha } from "../tokens";
import { Btn }      from "./Btn";
import { Badge }    from "./Badge";
import { ScoreRing } from "./ScoreRing";
import { useMotion, useReveal, useCountUp } from "../hooks/useMotion";
import { usePreviewMutation, usePreviewStatus } from "../hooks/useMatches";
import { useCredits, useDeepScorePaid, usePlans } from "../hooks/usePayments";
import { PricingModal } from "./PricingModal";
import { LockedAnalysisTeaser, type TeaserCta } from "./LockedAnalysisTeaser";
import type { MatchWithJob, ScoreBreakdown, MatchedSkill, MissingSkill, WorkAuthTag, FamilyFitSummary } from "@jobreel/shared";
import { matchDisplayTier, similarityLabel } from "@jobreel/shared";

// ── Helpers ──────────────────────────────────────────────────────────────

function deriveRecommendation(score: number): "apply" | "review" | "track" | "pass" {
  if (score >= 85) return "apply";
  if (score >= 70) return "review";
  if (score >= 50) return "track";
  return "pass";
}

function confidenceLabel(c: number | null): string {
  if (c == null) return "unknown";
  if (c > 0.75)  return "high";
  if (c > 0.5)   return "medium";
  return "low";
}

function formatSource(source: string): string {
  const map: Record<string, string> = {
    "vps-greenhouse": "Greenhouse API",
    "vps-ashby":      "Ashby API",
    "vps-lever":      "Lever API",
    "vps-career-page":"Company portal",
    "vps-remote-portals": "Remote portals",
    "extension-naukri":   "Naukri (extension)",
    "extension-linkedin": "LinkedIn (extension)",
    "extension-indeed":   "Indeed (extension)",
    "extension-iimjobs":  "IIMJobs (extension)",
  };
  return map[source] ?? source;
}

function timeAgo(isoStr: string | null): string {
  if (!isoStr) return "unknown";
  const hrs = Math.floor((Date.now() - new Date(isoStr).getTime()) / 3_600_000);
  if (hrs < 1)  return "just now";
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function flagToRisk(flag: string): { title: string; text: string } {
  const map: Record<string, { title: string; text: string }> = {
    salary_mismatch:    { title: "Compensation gap", text: "Posted salary range may fall below your target. Review the JD for flexibility signals before applying." },
    seniority_mismatch: { title: "Seniority stretch", text: "The role's seniority signals don't exactly match your resume level. This could be a stretch role — address it in the interview." },
    remote_mismatch:    { title: "Location / mode mismatch", text: "Work mode or location requirements conflict with your preferences." },
    skills_gap:         { title: "Skills gap detected", text: "One or more required skills from the JD are not evident in your active resume." },
    domain_mismatch:    { title: "Domain stretch", text: "The industry domain differs from your primary background. Transferable skills may still apply." },
  };
  return map[flag] ?? { title: flag.replace(/_/g, " "), text: "Caliber flagged this signal during scoring." };
}

// Maps the WorkAuthTag enum into a label + plain-English explanation,
// shown in the dedicated Location & work mode section.
const WORK_MODE_INFO: Record<WorkAuthTag, { label: string; detail: string; tone: "ok" | "info" | "warn" }> = {
  "remote-from-india":    { label: "Remote from India",     detail: "You can work from India for this role. India timezone signals look compatible.", tone: "ok"   },
  "india-onsite":         { label: "Onsite in India",       detail: "Role requires physical presence at an India office. Confirm city before applying.",            tone: "info" },
  "hybrid-india":         { label: "Hybrid in India",       detail: "Mix of office and remote within India. Check the JD for the office cadence.",                  tone: "info" },
  "relocation-sponsored": { label: "Relocation sponsored — as stated in posting", detail: "Check the target country, visa class, and family terms in the posting.", tone: "info" },
  "oci-friendly":         { label: "OCI friendly — as stated in posting",         detail: "Verify the posting and the country's specific OCI work rules.",       tone: "info" },
  "h1b-transfer":         { label: "H-1B transfer — as stated in posting",       detail: "Check the posting for cap-exempt status and timing.",                tone: "info" },
  "contract-remote":      { label: "Contract remote",       detail: "Fixed-term remote engagement, not a permanent role. Check duration, conversion, and benefits.", tone: "warn" },
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function SectionLabel({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
      <span style={{ fontFamily: T.mono, fontSize: 10, color: T.tealSoft,
        letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}>
        {children}
      </span>
      {sub && <span style={{ fontFamily: T.mono, fontSize: 10, color: T.t3 }}>· {sub}</span>}
      <span style={{ flex: 1, height: 1, background: T.b0 }}/>
    </div>
  );
}

interface BreakdownRowProps {
  label: string;
  score: number;
  weight: number;
  delay: number;
}
function BreakdownBar({ label, score, weight, delay }: BreakdownRowProps) {
  const safe = Number.isFinite(score) ? score : 0;   // belt-and-suspenders: never render NaN
  const animated = useCountUp(Math.round(safe), { delay, duration: 1000 });
  const col = scoreColor(safe);
  const m = useMotion();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <span style={{ fontFamily: T.body, fontSize: 13, color: T.t1, width: "clamp(80px, 30%, 130px)", flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 6, background: T.s2, borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          width: `${animated}%`, height: "100%",
          background: `linear-gradient(90deg, ${withAlpha(col, 0.53)}, ${col})`,
          borderRadius: 99,
          transition: m === 0 ? "none" : "width 0.1s linear",
        }}/>
      </div>
      <span style={{ fontFamily: T.mono, fontSize: 12, color: col, width: 30,
        textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
        {animated}
      </span>
      <span style={{ fontFamily: T.mono, fontSize: 9, color: T.t3, width: 30,
        textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {weight}%
      </span>
    </div>
  );
}

function SkillRow({ item, kind, delay }: { item: MatchedSkill | MissingSkill; kind: "matched" | "missing"; delay: number }) {
  const r = useReveal(delay, 4);
  const isMatched = kind === "matched";
  const m = item as MatchedSkill;
  const g = item as MissingSkill;
  const strengthCol = isMatched
    ? (m.strength === "core" ? T.ok : m.strength === "strong" ? T.tealSoft : T.t1)
    : (g.importance === "required" ? T.err : g.importance === "preferred" ? T.warn : T.t2);

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12,
      padding: "10px 0", borderBottom: `1px solid ${T.b0}`, ...r }}>
      <span style={{
        width: 8, height: 8, borderRadius: "50%", marginTop: 6, flexShrink: 0,
        background: isMatched ? strengthCol : "transparent",
        border: isMatched ? "none" : `1.5px solid ${strengthCol}`,
      }}/>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
          <span style={{ fontFamily: T.body, fontSize: 13.5, fontWeight: 600, color: T.t0 }}>{item.skill}</span>
          <span style={{ fontFamily: T.mono, fontSize: 9, color: strengthCol,
            letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, whiteSpace: "nowrap" }}>
            {isMatched ? m.strength : g.importance}
          </span>
        </div>
        <p style={{ fontFamily: T.body, fontSize: 12, color: T.t2, margin: 0, lineHeight: 1.5 }}>
          {isMatched ? m.evidence : g.note}
        </p>
      </div>
    </div>
  );
}

function RiskCard({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "12px 14px",
      background: T.warnBg, border: `1px solid ${T.warnBdr}`, borderRadius: 8 }}>
      <span style={{ color: T.warn, fontSize: 14, lineHeight: 1, marginTop: 2 }}>⚠</span>
      <div>
        <p style={{ fontFamily: T.body, fontSize: 13, color: T.t0, fontWeight: 600, margin: "0 0 3px" }}>{title}</p>
        <p style={{ fontFamily: T.body, fontSize: 12, color: T.t1, margin: 0, lineHeight: 1.55 }}>{text}</p>
      </div>
    </div>
  );
}

function RecommendationBanner({ recommendation, confidence, summary }: {
  recommendation: "apply" | "review" | "track" | "pass";
  confidence: string;
  summary: string | null;
}) {
  const map = {
    apply:  { label: "Apply with confidence", icon: <Star size={14}/>,    col: T.ok,       bg: T.okBg,   bdr: T.okBdr   },
    review: { label: "Worth a closer look",   icon: "◆",                  col: T.tealSoft, bg: T.tealBg, bdr: T.tealBdr },
    track:  { label: "Track for now",         icon: <Circle size={14}/>,  col: T.warn,     bg: T.warnBg, bdr: T.warnBdr },
    pass:   { label: "Likely a pass",         icon: "✕",                  col: T.err,      bg: T.errBg,  bdr: T.errBdr  },
  };
  const v = map[recommendation];
  return (
    <div style={{ padding: "18px 20px", background: v.bg, border: `1px solid ${v.bdr}`, borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: summary ? 8 : 0 }}>
        <span style={{ color: v.col, fontSize: 14 }}>{v.icon}</span>
        <span style={{ fontFamily: T.display, fontSize: 17, fontWeight: 600, color: v.col,
          letterSpacing: "-0.005em" }}>
          {v.label}
        </span>
        <span style={{ flex: 1 }}/>
        <span style={{ fontFamily: T.mono, fontSize: 9, color: T.t2,
          letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Confidence · <span style={{ color: T.t0 }}>{confidence}</span>
        </span>
      </div>
      {summary && (
        <p style={{ fontFamily: T.body, fontSize: 13.5, color: T.t1, margin: 0, lineHeight: 1.6 }}>
          {summary}
        </p>
      )}
    </div>
  );
}

function WorkModeFit({ tag, location }: { tag: WorkAuthTag | null; location: string | null }) {
  // Without a work_auth_tag we still want to surface the raw location string
  // so the user has *something* to act on.
  if (!tag) {
    return (
      <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 10, padding: "14px 16px" }}>
        <p style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, letterSpacing: "0.06em",
          textTransform: "uppercase", margin: "0 0 6px" }}>Work mode</p>
        <p style={{ fontFamily: T.body, fontSize: 13, color: T.t1, margin: 0, lineHeight: 1.55 }}>
          {location
            ? <>No work-auth signal detected. Location reported as <span style={{ color: T.t0, fontWeight: 600 }}>{location}</span> — confirm in the JD before applying.</>
            : <>No work-auth signal and no location reported. Open the portal to confirm where the role is based.</> }
        </p>
      </div>
    );
  }

  const info = WORK_MODE_INFO[tag];
  const toneCol = info.tone === "ok" ? T.ok : info.tone === "warn" ? T.warn : T.tealSoft;
  const toneBg  = info.tone === "ok" ? T.okBg : info.tone === "warn" ? T.warnBg : T.tealBg;
  const toneBdr = info.tone === "ok" ? T.okBdr : info.tone === "warn" ? T.warnBdr : T.tealBdr;

  return (
    <div style={{ background: toneBg, border: `1px solid ${toneBdr}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontFamily: T.display, fontSize: 14, color: toneCol, fontWeight: 600 }}>
          {info.label}
        </span>
        {location && (
          <span style={{ fontFamily: T.mono, fontSize: 10, color: T.t2, letterSpacing: "0.04em" }}>
            · {location}
          </span>
        )}
      </div>
      <p style={{ fontFamily: T.body, fontSize: 13, color: T.t1, margin: 0, lineHeight: 1.55 }}>
        {info.detail}
      </p>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: "12px 14px", background: T.s1, border: `1px dashed ${T.b1}`,
      borderRadius: 8, color: T.t2, fontFamily: T.body, fontSize: 12.5, lineHeight: 1.55 }}>
      {children}
    </div>
  );
}

function AwaitingScoring({ workAuthTag, location }: { workAuthTag: WorkAuthTag | null; location: string | null }) {
  return (
    <>
      <div style={{ padding: "18px 20px", background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Circle size={14} color={T.tealSoft}/>
          <span style={{ fontFamily: T.display, fontSize: 17, fontWeight: 600, color: T.t0,
            letterSpacing: "-0.005em" }}>
            Awaiting full scoring
          </span>
          <span style={{ flex: 1 }}/>
          <span style={{ fontFamily: T.mono, fontSize: 9, color: T.t2,
            letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Confidence · <span style={{ color: T.t0 }}>unknown</span>
          </span>
        </div>
        <p style={{ fontFamily: T.body, fontSize: 13.5, color: T.t1, margin: 0, lineHeight: 1.6 }}>
          Caliber hasn't scored this role against your active resume yet, so there's no skill match,
          gap analysis, or risk flags to show. You can still open the portal to read the JD, or save
          the role to revisit when scoring finishes.
        </p>
      </div>

      <section style={{ marginTop: 28 }}>
        <SectionLabel>Location &amp; work mode</SectionLabel>
        <WorkModeFit tag={workAuthTag} location={location}/>
      </section>
    </>
  );
}

// ── Drawer sections ─────────────────────────────────────────────────────────────

function DrawerHeader({ match, onClose }: { match: MatchWithJob; onClose: () => void }) {
  const score = Math.round(match.weightedScore);
  const [hClose, setHClose] = useState(false);
  return (
    <header style={{
      padding: "18px 24px 20px",
      borderBottom: `1px solid ${T.b0}`,
      background: `linear-gradient(180deg, ${T.s1} 0%, ${T.bg} 100%)`,
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
          <span style={{ fontFamily: T.mono, fontSize: 9.5, color: T.tealSoft,
            letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, flexShrink: 0 }}>
            Role Intelligence
          </span>
          <span style={{ color: T.t3, fontSize: 10, flexShrink: 0 }}>·</span>
          <span style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t2, letterSpacing: "0.06em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {formatSource(match.source)} · {timeAgo(match.postedAt)}
          </span>
        </div>
        <button
          onClick={onClose}
          onMouseEnter={() => setHClose(true)}
          onMouseLeave={() => setHClose(false)}
          style={{
            background: hClose ? T.s2 : "transparent",
            border: `1px solid ${T.b1}`,
            color: hClose ? T.t0 : T.t1, cursor: "pointer",
            width: 28, height: 28, borderRadius: 7,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontFamily: T.mono,
            transition: "all 0.15s",
          }}
        >✕</button>
      </div>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <ScoreRing score={score} size={72} delay={150}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: T.mono, fontSize: 11, color: T.tealSoft, margin: "0 0 4px", letterSpacing: "0.04em" }}>
            {match.company}
          </p>
          <h2 style={{
            fontFamily: T.display, fontSize: "clamp(20px, 2.5vw, 26px)",
            color: T.t0, fontWeight: 600, lineHeight: 1.2,
            margin: "0 0 10px", letterSpacing: "-0.01em",
          }}>
            {match.title}
          </h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {match.workAuthTag?.includes("remote") && <Badge variant="info" size="sm">Remote</Badge>}
            {match.location && <Badge variant="default" size="sm">{match.location}</Badge>}
            {match.salaryRaw && <Badge variant="default" size="sm">{match.salaryRaw}</Badge>}
            {match.postedAt && (
              <Badge variant="ok" size="sm" pulse>
                {timeAgo(match.postedAt)}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// ── Quick match (semantic-tier) panel ──────────────────────────────────────────────
const FAMILY_LABEL: Record<string, string> = {
  it_support: "IT Support", system_admin: "System Administration", network_admin: "Network Administration",
  infrastructure_ops: "Infrastructure Ops", devops_sre: "DevOps / SRE", software_engineering: "Software Engineering",
  qa_sdet: "QA / SDET", data_ai_ml: "Data / AI / ML", data_analyst: "Data Analytics / BI",
  database_administration: "Database Administration", security: "Security / Cyber",
  governance_risk_compliance: "Governance / Risk / Compliance",
  engineering_management: "Engineering Management", product_management: "Product Management",
  project_delivery: "Project / Delivery Management", non_it_business: "Non-technical", unknown: "Unclassified",
};
const familyLabel = (f?: string | null) => (f ? (FAMILY_LABEL[f] ?? f.replace(/_/g, " ")) : "—");
// Fit type, humanized. Raw multiplier/cosine are never shown.
const FIT_LABEL: Record<string, string> = {
  same: "Same field as your profile", matched_family: "One of your target fields",
  adjacent: "Closely related to your field", uncertain: "Related field",
  non_adjacent: "Adjacent field", no_penalty: "Matches your profile",
};
function QuickMatchPanel({ score, semanticScore, familyFit }: {
  score: number; semanticScore: number | null; familyFit: FamilyFitSummary | null;
}) {
  const sim = similarityLabel(semanticScore);
  return (
    <section>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 11px", borderRadius: 999,
        background: T.tealBg, border: `1px solid ${T.tealBdr}`, marginBottom: 18 }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: T.tealSoft }}/>
        <span style={{ fontFamily: T.body, fontSize: 11.5, color: T.tealSoft, fontWeight: 600, letterSpacing: 0.3 }}>Quick match</span>
        <span style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t2 }}>semantic pre-scoring</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <QuickRow label="Overall match" value={`${score} / 100`} accent={scoreColor(score)}/>
        {sim && <QuickRow label="Semantic similarity" value={sim}/>}
        {familyFit && (
          <QuickRow
            label="Role-family fit"
            value={familyLabel(familyFit.jobFamily)}
            sub={`${FIT_LABEL[familyFit.adjacencyType] ?? "Related"} · your profile: ${familyLabel(familyFit.candidateFamily)}`}
          />
        )}
      </div>

      <p style={{ fontFamily: T.body, fontSize: 12, color: T.t2, marginTop: 18, lineHeight: 1.7 }}>
        This is a fast semantic match. A full skill-by-skill breakdown (title, skills, seniority, domain fit)
        appears once deep AI scoring runs for this role.
      </p>
    </section>
  );
}
function QuickRow({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14,
      paddingBottom: 10, borderBottom: `1px solid ${T.s2}` }}>
      <span style={{ fontFamily: T.body, fontSize: 13, color: T.t1 }}>{label}</span>
      <div style={{ textAlign: "right" }}>
        <span style={{ fontFamily: T.mono, fontSize: 13, color: accent ?? T.t0, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</span>
        {sub && <div style={{ fontFamily: T.body, fontSize: 10.5, color: T.t2, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Free-preview CTA (shown on a quick-tier match) ────────────────────────────────────
const PREVIEW_ERR_MSG: Record<string, string> = {
  score_below_floor:     "This match isn't strong enough for a free deep analysis.",
  wrong_family:          "This role isn't in your target field.",
  not_open:              "This posting is no longer open.",
  profile_unclear:       "Add or clarify your target roles first.",
  already_deep_scored:   "This match is already fully analyzed.",
  preview_limit_reached: "You've already used your free preview.",
  not_preview_eligible:  "This match isn't eligible for a free preview.",
  family_pool_too_thin:  "Not enough matching jobs yet to analyze.",
  telemetry_not_ready:   "Deep analysis is temporarily unavailable.",
  cost_cap_exceeded:     "Deep analysis is temporarily unavailable.",
};

function UnlockButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px",
      background: disabled ? T.s3 : T.teal, color: disabled ? T.t2 : "#04120E", border: "none", borderRadius: 7,
      fontFamily: T.body, fontSize: 12.5, fontWeight: 700, cursor: disabled ? "default" : "pointer" }}>
      {label}
    </button>
  );
}

function PreviewSampleBanner({ credits, onBuy, onUseCredit, paidPending }: {
  credits: number; onBuy: () => void; onUseCredit: () => void; paidPending: boolean;
}) {
  const hasCredits = credits > 0;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between",
      padding: "12px 14px", marginBottom: 18, borderRadius: 10, background: T.tealBg, border: `1px solid ${T.tealBdr}` }}>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontFamily: T.body, fontSize: 12.5, color: T.tealSoft, fontWeight: 700, margin: 0 }}>Your free deep analysis</p>
        <p style={{ fontFamily: T.body, fontSize: 11.5, color: T.t1, margin: "2px 0 0", lineHeight: 1.5 }}>
          {hasCredits ? "Use a credit to run this depth on your other matches." : "Get this depth on any match with credits."}
        </p>
      </div>
      {hasCredits
        ? <UnlockButton label={paidPending ? "Analyzing…" : "Use 1 credit"} onClick={onUseCredit} disabled={paidPending}/>
        : <UnlockButton label="Get credits · from ₹99" onClick={onBuy}/>}
    </div>
  );
}

function DrawerBody({ match: propMatch }: { match: MatchWithJob }) {
  // The free-preview flow swaps in the LLM-scored match on success so the drawer
  // re-renders the full breakdown without a parent round-trip. Reset when the user
  // opens a different match.
  const preview       = usePreviewMutation();
  const previewStatus = usePreviewStatus();
  const [previewed, setPreviewed] = useState<MatchWithJob | null>(null);
  useEffect(() => { setPreviewed(null); }, [propMatch.matchId]);
  const match = previewed ?? propMatch;

  const runPreview = () => preview.mutate(match.matchId, { onSuccess: (res) => setPreviewed(res.match) });
  const previewErrCode = (() => {
    const e = preview.error as { response?: { data?: { code?: string; eligibilityReason?: string } } } | null;
    return e?.response?.data?.eligibilityReason ?? e?.response?.data?.code ?? null;
  })();

  // Paid path: spend a credit to deep-score (mirrors the free preview — swaps in
  // the scored match), or open the paywall when the balance is 0.
  const creditsQ  = useCredits();
  const deepScore = useDeepScorePaid();
  const [showPricing, setShowPricing] = useState(false);
  const credits   = creditsQ.data?.credits ?? 0;
  const runPaid   = () => deepScore.mutate(match.matchId, { onSuccess: (res) => setPreviewed(res.match) });
  const paidErr   = deepScore.isError ? "Couldn't run the analysis — your credit was not used." : null;
  const pricingModal = <PricingModal open={showPricing} onClose={() => setShowPricing(false)} />;

  // One CTA drives the locked teaser across all states: free preview → use a
  // credit → buy. Null only when nothing is available (free off + no credits +
  // payments off) — then the teaser isn't shown.
  const plansEnabled = usePlans().data?.enabled ?? false;
  const teaserCta: TeaserCta | null = (() => {
    if ((previewStatus.data?.remaining ?? 0) > 0)
      return { label: preview.isPending ? "Analyzing…" : "See full analysis · 1 free", sub: "Your 1 free deep analysis", disabled: preview.isPending, onClick: runPreview };
    if (credits > 0)
      return { label: deepScore.isPending ? "Analyzing…" : "Use 1 credit · Deep analysis", sub: `${credits} credit${credits === 1 ? "" : "s"} left`, disabled: deepScore.isPending, onClick: runPaid };
    if (plansEnabled)
      return { label: "Unlock deep analysis · from ₹99", sub: "Credits never expire", onClick: () => setShowPricing(true) };
    return null;
  })();
  const teaserError = previewErrCode ? (PREVIEW_ERR_MSG[previewErrCode] ?? "Couldn't run the analysis right now.") : paidErr;

  const score     = Math.round(match.weightedScore);
  const rec       = deriveRecommendation(score);
  const confLabel = confidenceLabel(match.confidence);
  const bd        = match.scoreBreakdown as ScoreBreakdown | null;
  const flags     = bd?.flags ?? [];

  // TIER: only an llm_scored row has the six weighted sub-scores. A semantic-only
  // row (semantic_only / auto_semantic_only / fallback_semantic_only / needs_jd)
  // has no LLM sub-scores — it may carry `familyFit`, or be a null breakdown that
  // still has a real calibrated weightedScore. Gate the breakdown on `title`
  // presence (not on `bd != null`) so we never render NaN dimension bars.
  const tier            = matchDisplayTier(match);   // "llm" | "quick" | "awaiting"
  const hasLlmBreakdown = tier === "llm";
  const isQuickMatch    = tier === "quick";          // semantic / auto / needs_jd-with-score

  // Scores from Claude are already 0-100
  const breakdownRows: { label: string; score: number; weight: number }[] = hasLlmBreakdown ? [
    { label: "Title fit",       score: bd!.title!,     weight: 30 },
    { label: "Skills fit",      score: bd!.skills!,    weight: 25 },
    { label: "Seniority fit",   score: bd!.seniority!, weight: 20 },
    { label: "Domain fit",      score: bd!.industry!,  weight: 10 },
    { label: "Company fit",     score: bd!.company!,   weight: 10 },
    { label: "Location / mode", score: bd!.workMode!,  weight: 5  },
  ] : [];

  const matchedSkills = bd?.matchedSkills ?? [];
  const missingSkills = bd?.missingSkills ?? [];
  const resumeOverlap = bd?.resumeOverlap ?? null;
  const risks         = flags.map(flagToRisk);

  const actionText: Record<"apply" | "review" | "track" | "pass", string> = {
    apply:  `Strong overall fit (${score}) with ${confLabel} confidence. Open the portal to read the JD in full and submit through the company's site. Caliber never applies on your behalf.`,
    review: `Good fit (${score}) worth exploring. Review the JD carefully and apply if it aligns with your goals. Open the portal to read the full posting.`,
    track:  `Moderate fit (${score}). Track this role to monitor for changes — recheck if your resume or the JD updates. Skip if you have stronger matches.`,
    pass:   `Low fit (${score}). The role has signals that don't align well with your profile. Skip unless you have a specific reason to apply.`,
  };

  const r = useReveal(0, 6);

  // Quick match (semantic tier): a real calibrated score but no LLM sub-scores.
  // Show the honest panel — overall score (header) + semantic similarity + family
  // fit — instead of NaN dimension bars or a misleading "awaiting" state.
  if (isQuickMatch) {
    return (
      <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 24px) 32px", ...r }}>
        <QuickMatchPanel score={score} semanticScore={match.semanticScore} familyFit={bd?.familyFit ?? null}/>
        {teaserCta && <LockedAnalysisTeaser cta={teaserCta} error={teaserError}/>}
        <section style={{ marginTop: 28 }}>
          <SectionLabel>Location &amp; work mode</SectionLabel>
          <WorkModeFit tag={match.workAuthTag} location={match.location}/>
        </section>
        <p style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, marginTop: 24, lineHeight: 1.7 }}>
          Source: {formatSource(match.source)} · scanned {timeAgo(match.postedAt)} · Caliber never applies on your behalf.
        </p>
        {pricingModal}
      </div>
    );
  }

  // Awaiting-scoring fallback: no LLM breakdown AND no meaningful score — surface
  // a calm explainer. Still show work mode + source so the drawer isn't empty.
  if (!hasLlmBreakdown) {
    return (
      <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 24px) 32px", ...r }}>
        <AwaitingScoring workAuthTag={match.workAuthTag} location={match.location}/>
        <p style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, marginTop: 24, lineHeight: 1.7 }}>
          Source: {formatSource(match.source)} · scanned {timeAgo(match.postedAt)} · Caliber never applies on your behalf.
        </p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 24px) 32px", ...r }}>

      {/* Celebrated unlock — shown right after the user reveals this analysis */}
      {previewed && (
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 14px", marginBottom: 16,
          borderRadius: 10, background: T.okBg, border: `1px solid ${T.okBdr}` }}>
          <Sparkles size={15} color={T.ok} style={{ flexShrink: 0 }}/>
          <span style={{ fontFamily: T.body, fontSize: 12.5, color: T.t0, fontWeight: 600 }}>Deep analysis unlocked</span>
          <span style={{ fontFamily: T.body, fontSize: 11.5, color: T.t1 }}>— here's your full fit for this role.</span>
        </div>
      )}

      {/* Preview-sample banner — shown when this breakdown came from the free preview */}
      {match.modelUsed === "llm_scored:preview" && (
        <PreviewSampleBanner credits={credits} onBuy={() => setShowPricing(true)} onUseCredit={runPaid} paidPending={deepScore.isPending}/>
      )}

      {/* Recommendation */}
      <RecommendationBanner recommendation={rec} confidence={confLabel} summary={match.explanation}/>

      {/* Location & work mode — always present once scoring exists */}
      <section style={{ marginTop: 28 }}>
        <SectionLabel>Location &amp; work mode</SectionLabel>
        <WorkModeFit tag={match.workAuthTag} location={match.location}/>
      </section>

      {/* Score breakdown */}
      {breakdownRows.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <SectionLabel>Score breakdown</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {breakdownRows.map((row, i) => (
              <BreakdownBar key={row.label} label={row.label} score={row.score} weight={row.weight} delay={200 + i * 80}/>
            ))}
          </div>
          {/* Salary + seniority signals */}
          {(bd?.salarySignal || bd?.seniorityDelta != null) && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              {bd?.salarySignal && bd.salarySignal !== "unknown" && (() => {
                const salMap = {
                  above_target: { label: "Above target salary", col: T.ok   },
                  at_target:    { label: "At target salary",    col: T.tealSoft },
                  below_target: { label: "Below target salary", col: T.warn },
                };
                const s = salMap[bd.salarySignal as keyof typeof salMap];
                return s ? (
                  <span style={{ fontFamily: T.mono, fontSize: 9, color: s.col,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    background: withAlpha(s.col, 0.09), border: `1px solid ${withAlpha(s.col, 0.25)}`,
                    borderRadius: 6, padding: "3px 8px" }}>
                    {s.label}{bd.salaryRangeDetected ? ` · ${bd.salaryRangeDetected}` : ""}
                  </span>
                ) : null;
              })()}
              {bd?.seniorityDelta != null && bd.seniorityDelta !== 0 && (() => {
                const delta = bd.seniorityDelta!;
                const label = delta > 0
                  ? `Overqualified ${delta > 1 ? "(significantly)" : "(slightly)"}`
                  : `Underqualified ${delta < -1 ? "(significantly)" : "(slightly)"}`;
                const col = Math.abs(delta) > 1 ? T.warn : T.t2;
                return (
                  <span style={{ fontFamily: T.mono, fontSize: 9, color: col,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    background: withAlpha(col, 0.09), border: `1px solid ${withAlpha(col, 0.25)}`,
                    borderRadius: 6, padding: "3px 8px" }}>
                    {label}
                    {bd.candidateSeniorityDetected && bd.jobSeniorityDetected
                      ? ` · ${bd.candidateSeniorityDetected} → ${bd.jobSeniorityDetected}`
                      : ""}
                  </span>
                );
              })()}
            </div>
          )}
          <p style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, marginTop: 14, lineHeight: 1.6 }}>
            Weights reflect your profile. Skills + title + seniority count for 75% of the overall score.
          </p>
        </section>
      )}

      {/* Why this matched */}
      <section style={{ marginTop: 32 }}>
        {matchedSkills.length > 0
          ? <SectionLabel sub={`${matchedSkills.length} matched`}>Why this matched</SectionLabel>
          : <SectionLabel>Why this matched</SectionLabel>}
        {resumeOverlap != null && (
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.t1, margin: "0 0 10px", lineHeight: 1.55 }}>
            <span style={{ color: T.tealSoft, fontWeight: 600 }}>{resumeOverlap}% resume overlap</span>
            {" "}· evidence drawn from your active resume
          </p>
        )}
        {matchedSkills.length > 0 ? (
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 10, padding: "4px 16px" }}>
            {matchedSkills.map((s, i) => (
              <SkillRow key={s.skill} item={s} kind="matched" delay={300 + i * 60}/>
            ))}
          </div>
        ) : (
          <EmptyHint>
            No itemised skill matches returned. The overall score above still reflects title, seniority,
            and domain signals — open the JD to read the requirements in full.
          </EmptyHint>
        )}
      </section>

      {/* What's missing */}
      {missingSkills.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <SectionLabel sub={`${missingSkills.length}`}>What's missing</SectionLabel>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.t1, margin: "0 0 10px", lineHeight: 1.55 }}>
            Gaps Caliber detected — review before applying.
          </p>
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 10, padding: "4px 16px" }}>
            {missingSkills.map((s, i) => (
              <SkillRow key={s.skill} item={s} kind="missing" delay={500 + i * 60}/>
            ))}
          </div>
        </section>
      )}

      {/* Risk flags */}
      <section style={{ marginTop: 32 }}>
        <SectionLabel sub={risks.length > 0 ? `${risks.length} flagged` : "none"}>Risk signals</SectionLabel>
        {risks.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {risks.map((r, i) => <RiskCard key={i} title={r.title} text={r.text}/>)}
          </div>
        ) : (
          <div style={{ padding: "12px 14px", background: T.okBg, border: `1px solid ${T.okBdr}`,
            borderRadius: 8, display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ color: T.ok, fontSize: 14 }}>✓</span>
            <p style={{ fontFamily: T.body, fontSize: 13, color: T.t1, margin: 0, lineHeight: 1.55 }}>
              No risks flagged. Caliber didn't detect salary, seniority, location, or domain mismatches.
            </p>
          </div>
        )}
      </section>

      {/* Suggested action */}
      <section style={{ marginTop: 32, padding: "16px 18px", background: T.s1,
        border: `1px solid ${T.b0}`, borderRadius: 10 }}>
        <p style={{ fontFamily: T.mono, fontSize: 10, color: T.t3,
          letterSpacing: "0.06em", textTransform: "uppercase", margin: "0 0 8px" }}>
          Suggested action
        </p>
        <p style={{ fontFamily: T.body, fontSize: 13.5, color: T.t1, margin: 0, lineHeight: 1.6 }}>
          {actionText[rec]}
        </p>
      </section>

      {/* Footnote */}
      <p style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, marginTop: 24, lineHeight: 1.7 }}>
        Source: {formatSource(match.source)} · scanned {timeAgo(match.postedAt)} · scored against your active resume · Caliber never applies on your behalf.
      </p>
      {pricingModal}
    </div>
  );
}

function DrawerFooter({ match, onTrack, onSave, onClose }: {
  match:    MatchWithJob;
  onTrack?: (matchId: string) => void;
  onSave?:  (matchId: string) => void;
  onClose?: () => void;
}) {
  return (
    <footer style={{
      padding: "14px 24px",
      borderTop: `1px solid ${T.b0}`,
      background: T.s1,
      // wrap: on a 390px viewport the four actions do not fit one row, and the
      // primary "Open portal" CTA was pushed off-screen (found by the MATCH
      // mobile journey). Wrapping keeps every action reachable at any width.
      display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
      flexShrink: 0,
    }}>
      <Btn variant="ghost" size="md" onClick={onClose}>Skip</Btn>
      <Btn variant="ghost" size="md" onClick={() => onSave?.(match.matchId)}>Save</Btn>
      <span style={{ flex: 1 }}/>
      <Btn variant="outline" size="md" onClick={() => onTrack?.(match.matchId)}>Track manually</Btn>
      {match.url ? (
        <Btn variant="cta" size="md" icon={<ExternalLink size={13} strokeWidth={1.8}/>} onClick={() => openExternal(match.url)}>
          Open portal
        </Btn>
      ) : (
        <span title="No portal URL on this job — track manually"
          style={{ fontFamily: T.mono, fontSize: 10, color: T.t3,
            letterSpacing: "0.06em", textTransform: "uppercase", padding: "0 6px" }}>
          no portal url
        </span>
      )}
    </footer>
  );
}

// ── Public component ────────────────────────────────────────────────────────────────

interface Props {
  open:     boolean;
  match:    MatchWithJob | null;
  onClose:  () => void;
  onTrack?: (matchId: string) => void;
  onSave?:  (matchId: string) => void;
}

export function MatchDetailDrawer({ open, match, onClose, onTrack, onSave }: Props) {
  const m = useMotion();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setShown(true));
      const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
      window.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = prev;
      };
    } else {
      setShown(false);
    }
  }, [open, onClose]);

  if (!open || !match) return null;

  const dur      = m === 0 ? 0 : 0.35 * m;
  const isMobile = window.innerWidth < 720;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, fontFamily: T.body }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(8,8,12,0.55)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          opacity: shown ? 1 : 0,
          transition: `opacity ${dur}s ease`,
        }}
      />

      {/* Drawer */}
      <aside style={{
        position: "absolute",
        top:    isMobile ? "auto" : 0,
        bottom: 0,
        right:  0,
        left:   isMobile ? 0 : "auto",
        width:  isMobile ? "100%" : "min(680px, 92vw)",
        height: isMobile ? "94vh" : "100vh",
        background: T.bg,
        borderLeft: isMobile ? "none" : `1px solid ${T.b1}`,
        borderTopLeftRadius:  isMobile ? 16 : 0,
        borderTopRightRadius: isMobile ? 16 : 0,
        boxShadow: "-30px 0 60px rgba(0,0,0,0.4)",
        display: "flex", flexDirection: "column",
        transform: shown
          ? "translate(0,0)"
          : isMobile ? "translateY(100%)" : "translateX(100%)",
        transition: `transform ${dur}s cubic-bezier(.2,.7,.2,1)`,
      }}>
        <DrawerHeader match={match} onClose={onClose}/>
        <DrawerBody match={match}/>
        <DrawerFooter
        match={match}
        onClose={onClose}
        {...(onTrack && { onTrack })}
        {...(onSave  && { onSave  })}
      />
      </aside>
    </div>
  );
}
